import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import NoticeRegion from '../../../common/components/notice-region/notice-region';
import { APP_EVENTS } from '../../../core/constants/events';

const NOTICE_TTL_MS = 5_000;
const NOTICE_MAX = 3;

const NoticeContext = createContext(null);

export function NoticeProvider({ children }) {
    const [notices, setNotices] = useState([]);
    const counter = useRef(0);
    const timers = useRef(new Map());

    const dismiss = useCallback((id) => {
        clearTimeout(timers.current.get(id));
        timers.current.delete(id);
        setNotices((current) => current.filter((notice) => notice.id !== id));
    }, []);

    const notify = useCallback(({ tone = 'info', message }) => {
        if (!message) return;
        counter.current += 1;
        const id = counter.current;
        setNotices((current) => [...current, { id, tone, message }].slice(-NOTICE_MAX));
        timers.current.set(id, setTimeout(() => dismiss(id), NOTICE_TTL_MS));
    }, [dismiss]);

    useEffect(() => {
        const pending = timers.current;
        const handleNotice = (event) => notify(event.detail || {});
        window.addEventListener(APP_EVENTS.NOTICE, handleNotice);
        return () => {
            window.removeEventListener(APP_EVENTS.NOTICE, handleNotice);
            for (const timer of pending.values()) clearTimeout(timer);
            pending.clear();
        };
    }, [notify]);

    const value = useMemo(() => ({ notify, dismiss }), [notify, dismiss]);

    return (
        <NoticeContext.Provider value={value}>
            {children}
            <NoticeRegion notices={notices} onDismiss={dismiss} />
        </NoticeContext.Provider>
    );
}

export function useNotice() {
    const context = useContext(NoticeContext);
    if (!context) throw new Error('useNotice must be used inside <NoticeProvider>');
    return context;
}
